import { useEffect, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import {
  NavigationHistoryContext,
  type HistoryEntry,
} from './navigationHistoryContext';

const STORAGE_KEY = 'nav-history-stack';

const EXCLUDED_PATHS = new Set(['/not-found']);

// Navigating to these paths always resets the breadcrumb stack (fresh start).
const RESET_PATHS = new Set(['/Panel-główny', '/']);

const isExcluded = (pathname: string) => EXCLUDED_PATHS.has(pathname);
const isReset = (pathname: string) => RESET_PATHS.has(pathname);

/**
 * Builds a minimal synthetic stack from a pathname so the breadcrumb always
 * shows at least the section parent, even on a direct URL open or hard refresh
 * with no session data.
 *
 * e.g. /Zgłoszenia/Podgląd-zgłoszenia-kontaktowego/uuid
 *   → ['/Zgłoszenia', '/Zgłoszenia/Podgląd-zgłoszenia-kontaktowego/uuid']
 */
function buildSyntheticStack(pathname: string): HistoryEntry[] {
  const sectionRoot = `/${pathname.split('/').filter(Boolean)[0] ?? ''}`;
  if (sectionRoot && sectionRoot !== pathname) {
    return [{ pathname: sectionRoot }, { pathname }];
  }
  return [{ pathname }];
}

function getInitialStack(pathname: string): HistoryEntry[] {
  if (isExcluded(pathname) || isReset(pathname)) {
    return [{ pathname }];
  }
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed: HistoryEntry[] = JSON.parse(stored);
      const filtered = Array.isArray(parsed)
        ? parsed.filter((e) => !isExcluded(e.pathname))
        : [];
      if (filtered.length > 0) {
        // Last entry matches current page — restore as-is
        if (filtered[filtered.length - 1]?.pathname === pathname) {
          return filtered;
        }
        // Current page exists earlier in the stack — trim to it
        const idx = filtered.findIndex((e) => e.pathname === pathname);
        if (idx !== -1) {
          return filtered.slice(0, idx + 1);
        }
        // Current pathname is unrelated to the stored trail (e.g. address-bar
        // paste or external redirect into the app). Treat as a fresh
        // navigation and synthesise a stack from the section root instead of
        // appending to the stale prior trail.
        return buildSyntheticStack(pathname);
      }
    }
  } catch {
    // ignore storage errors
  }
  // No sessionStorage data — synthesise the section root as the parent entry
  // so the breadcrumb shows the parent even on a direct URL open or hard refresh.
  return buildSyntheticStack(pathname);
}

export function NavigationHistoryProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const navType = useNavigationType();
  const [stack, setStack] = useState<HistoryEntry[]>(() =>
    getInitialStack(location.pathname)
  );

  useEffect(() => {
    setStack((prev) => {
      if (isExcluded(location.pathname)) {
        return prev;
      }

      const entry = {
        pathname: location.pathname,
        ...(location.search ? { search: location.search } : {}),
      };

      // Home/dashboard and explicit fresh-start navigations always reset the stack
      if (isReset(location.pathname)) {
        return [entry];
      }

      const isFreshStart = (location.state as { freshStart?: boolean } | null)
        ?.freshStart;

      if (isFreshStart) {
        return [entry];
      }

      if (navType === 'POP') {
        // If the current pathname is already the last entry, this is a page
        // refresh (browser fires POP on initial load) — keep the stack as-is.
        const last = prev[prev.length - 1];
        if (last?.pathname === location.pathname) {
          return prev;
        }
        // Destination exists earlier in the stack → back button, trim to it.
        // Destination not in stack → forward button restoring a popped entry, append it.
        const existingIdx = prev.findIndex(
          (e) => e.pathname === location.pathname
        );
        if (existingIdx !== -1) {
          return prev.slice(0, existingIdx + 1);
        }
        return [...prev, entry];
      }

      if (navType === 'REPLACE') {
        return [...prev.slice(0, -1), entry];
      }

      // PUSH — if the destination already exists in the stack, trim back to it
      const existingIndex = prev.findIndex(
        (e) => e.pathname === location.pathname
      );
      if (existingIndex !== -1) {
        return prev.slice(0, existingIndex + 1);
      }

      // Avoid consecutive duplicates
      const last = prev[prev.length - 1];
      if (last?.pathname === location.pathname) {
        return prev;
      }
      return [...prev, entry];
    });
  }, [location.pathname, location.search, location.state, navType]);

  // Persist to sessionStorage on every stack change
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stack));
    } catch {
      // ignore storage errors
    }
  }, [stack]);

  return (
    <NavigationHistoryContext.Provider value={{ stack }}>
      {children}
    </NavigationHistoryContext.Provider>
  );
}
